import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface PaymentConfigProps {
  domain: string;
}

interface PaymentConfigData {
  upiId: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  accountHolderName: string;
  enabledMethods: string[];
  lastUpdated: number;
}

const PaymentConfig: React.FC<PaymentConfigProps> = ({ domain }) => {
  const [config, setConfig] = useState<PaymentConfigData>({
    upiId: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    enabledMethods: ['upi', 'netbanking', 'card'],
    lastUpdated: 0,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadPaymentConfig();
  }, [domain]);

  const loadPaymentConfig = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, `school-data/${domain}/school-config/payment-config`);
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        setConfig(configDoc.data() as PaymentConfigData);
      }
    } catch (error) {
      console.error('Error loading payment config:', error);
      setErrorMessage('Failed to load payment configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PaymentConfigData, value: string) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleMethodToggle = (method: string) => {
    setConfig((prev) => {
      const enabledMethods = prev.enabledMethods.includes(method)
        ? prev.enabledMethods.filter((m) => m !== method)
        : [...prev.enabledMethods, method];
      return { ...prev, enabledMethods };
    });
    setSuccessMessage('');
    setErrorMessage('');
  };

  const validateForm = (): boolean => {
    if (!config.accountHolderName.trim()) {
      setErrorMessage('Account Holder Name is required');
      return false;
    }

    if (!config.bankName.trim()) {
      setErrorMessage('Bank Name is required');
      return false;
    }

    if (!config.accountNumber.trim()) {
      setErrorMessage('Account Number is required');
      return false;
    }

    if (config.accountNumber.length < 9 || config.accountNumber.length > 18) {
      setErrorMessage('Account Number must be between 9-18 digits');
      return false;
    }

    if (!config.ifscCode.trim()) {
      setErrorMessage('IFSC Code is required');
      return false;
    }

    // IFSC Code validation (11 characters, first 4 letters, 5th is 0, last 6 alphanumeric)
    const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscPattern.test(config.ifscCode.toUpperCase())) {
      setErrorMessage('Invalid IFSC Code format');
      return false;
    }

    if (config.enabledMethods.includes('upi') && !config.upiId.trim()) {
      setErrorMessage('UPI ID is required when UPI payment method is enabled');
      return false;
    }

    // UPI ID validation
    if (config.upiId.trim() && !config.upiId.includes('@')) {
      setErrorMessage('Invalid UPI ID format (e.g., yourschool@paytm)');
      return false;
    }

    if (config.enabledMethods.length === 0) {
      setErrorMessage('Please enable at least one payment method');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const configRef = doc(db, `school-data/${domain}/school-config/payment-config`);
      const updatedConfig = {
        ...config,
        ifscCode: config.ifscCode.toUpperCase(),
        lastUpdated: Date.now(),
      };

      await setDoc(configRef, updatedConfig);

      setConfig(updatedConfig);
      setSuccessMessage('✅ Payment configuration saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving payment config:', error);
      setErrorMessage('❌ Failed to save payment configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading payment configuration...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <div className="card shadow-sm">
            <div className="card-header bg-primary-custom text-white">
              <h5 className="mb-0">
                <i className="bi bi-credit-card me-2"></i>Payment Configuration
              </h5>
              <small>Configure bank account and payment methods for fee collection</small>
            </div>

            <div className="card-body">
              {/* Success/Error Messages */}
              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  {successMessage}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccessMessage('')}
                  ></button>
                </div>
              )}

              {errorMessage && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {errorMessage}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setErrorMessage('')}
                  ></button>
                </div>
              )}

              {/* Bank Account Details */}
              <h6 className="mb-3 text-primary">
                <i className="bi bi-bank me-2"></i>Bank Account Details
              </h6>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label fw-bold">
                    Account Holder Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., ABC School Trust"
                    value={config.accountHolderName}
                    onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">
                    Bank Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., State Bank of India"
                    value={config.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold">
                    Account Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., 1234567890"
                    value={config.accountNumber}
                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    maxLength={18}
                  />
                  <small className="text-muted">9-18 digits</small>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-bold">
                    IFSC Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control text-uppercase"
                    placeholder="e.g., SBIN0001234"
                    value={config.ifscCode}
                    onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                    maxLength={11}
                  />
                  <small className="text-muted">11 characters (e.g., SBIN0001234)</small>
                </div>
              </div>

              <hr />

              {/* UPI Details */}
              <h6 className="mb-3 text-primary">
                <i className="bi bi-phone me-2"></i>UPI Details
              </h6>

              <div className="row mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-bold">UPI ID</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., yourschool@paytm"
                    value={config.upiId}
                    onChange={(e) => handleInputChange('upiId', e.target.value)}
                  />
                  <small className="text-muted">Required if UPI method is enabled</small>
                </div>
              </div>

              <hr />

              {/* Payment Methods */}
              <h6 className="mb-3 text-primary">
                <i className="bi bi-toggles me-2"></i>Enabled Payment Methods
              </h6>

              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="methodUPI"
                      checked={config.enabledMethods.includes('upi')}
                      onChange={() => handleMethodToggle('upi')}
                    />
                    <label className="form-check-label" htmlFor="methodUPI">
                      <i className="bi bi-phone-fill me-2 text-success"></i>
                      <strong>UPI Payment</strong>
                      <small className="text-muted d-block">Accept payments via UPI apps</small>
                    </label>
                  </div>

                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="methodNetbanking"
                      checked={config.enabledMethods.includes('netbanking')}
                      onChange={() => handleMethodToggle('netbanking')}
                    />
                    <label className="form-check-label" htmlFor="methodNetbanking">
                      <i className="bi bi-bank me-2 text-primary"></i>
                      <strong>Net Banking</strong>
                      <small className="text-muted d-block">Accept payments via internet banking</small>
                    </label>
                  </div>

                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="methodCard"
                      checked={config.enabledMethods.includes('card')}
                      onChange={() => handleMethodToggle('card')}
                    />
                    <label className="form-check-label" htmlFor="methodCard">
                      <i className="bi bi-credit-card-fill me-2 text-warning"></i>
                      <strong>Debit/Credit Card</strong>
                      <small className="text-muted d-block">Accept card payments</small>
                    </label>
                  </div>
                </div>
              </div>

              {/* Last Updated Info */}
              {config.lastUpdated > 0 && (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Last updated: {new Date(config.lastUpdated).toLocaleString()}
                </div>
              )}

              {/* Save Button */}
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>Save Configuration
                    </>
                  )}
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={loadPaymentConfig}
                  disabled={saving}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>Reset
                </button>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="card shadow-sm mt-4">
            <div className="card-body">
              <h6 className="text-primary mb-3">
                <i className="bi bi-shield-check me-2"></i>Security Note
              </h6>
              <ul className="text-muted small mb-0">
                <li>Your bank account details are stored securely in Firestore</li>
                <li>Only authorized admins can view and modify payment configuration</li>
                <li>Never share your UPI ID or bank details publicly</li>
                <li>Regularly verify payment method settings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfig;
